/* Hallmark · component: provider-rows · genre: modern-minimal · theme: project tokens (orange --primary)
 * states: default · hover · focus · active · disabled · loading · error · success(n/a)
 * contrast: pass (46–50) — focus ring uses --primary-ink (0.55 0.19 40.5), ~4.5:1 on white
 */
import { useEffect, useState } from 'react';

import SocialiteController from '@/actions/App/Http/Controllers/User/Auth/SocialiteController';
import SocialiteInertiaController from '@/actions/App/Http/Controllers/User/Auth/SocialiteInertiaController';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { get } from '@/lib/axios';

type ProviderKey = 'zalo' | 'google';

interface SocialProvider {
  key: ProviderKey;
  name: string;
  icon: string;
  text: string;
}

type SocialLoginStatus = Record<ProviderKey, boolean>;

const PROVIDER_META: Record<ProviderKey, Omit<SocialProvider, 'key'>> = {
  zalo: {
    name: 'Zalo',
    icon: '/assets/templates/basic/images/zalo.svg',
    text: 'Zalo',
  },
  google: {
    name: 'Google',
    icon: '/assets/templates/basic/images/google.svg',
    text: 'Google',
  },
};

interface SocialLoginButtonsProps {
  className?: string;
  showDivider?: boolean;
  /** Relative path the user is returned to after the OAuth flow completes. */
  redirectTo?: string;
}

export default function SocialLoginButtons({ className = '', showDivider = true, redirectTo }: SocialLoginButtonsProps) {
  const [providers, setProviders] = useState<SocialProvider[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchProviders = () => {
    setLoading(true);
    setError(false);

    get<SocialLoginStatus>(SocialiteInertiaController.getSocialLoginStatus().url)
      .then((response) => {
        if (!response.success) {
          setError(true);
          return;
        }

        const available = (Object.keys(PROVIDER_META) as ProviderKey[])
          .filter((key) => response.data[key])
          .map((key) => ({ key, ...PROVIDER_META[key] }));

        setProviders(available);
      })
      .catch((err) => {
        console.error('Failed to fetch social login providers:', err);
        setError(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchProviders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSocialLogin = (providerKey: ProviderKey) => {
    const options = redirectTo ? { query: { redirect: redirectTo } } : undefined;
    window.location.href = SocialiteController.socialLogin(providerKey, options).url;
  };

  // Loading state — two skeleton rows to prevent reflow when data arrives
  if (loading) {
    return (
      <div className={cn('space-y-3', className)}>
        <div className="h-14 w-full rounded-xl bg-muted animate-pulse" />
        <div className="h-14 w-full rounded-xl bg-muted animate-pulse" />
      </div>
    );
  }

  if (error) {
    return (
      <div className={cn('rounded-xl border border-border bg-background p-4 text-center', className)}>
        <p className="mb-2 text-sm text-muted-foreground">Không thể tải phương thức đăng nhập nhanh</p>
        <Button type="button" variant="outline" size="sm" onClick={fetchProviders}>
          Thử lại
        </Button>
      </div>
    );
  }

  if (providers.length === 0) {
    return null;
  }

  return (
    <div className={cn('', className)}>
      <p className="mb-3 text-sm text-muted-foreground">Bắt đầu nhanh với</p>

      <div className="space-y-3">
        {providers.map((provider) => (
          <Button
            key={provider.key}
            type="button"
            variant="outline"
            className={cn(
              // Klook row: full-width, tall, icon pinned left, label centred
              'relative h-12 w-full rounded-xl border-border bg-background',
              // State: hover
              'hover:bg-accent hover:border-foreground/20',
              // State: focus — use --primary-ink (≥4.5:1 on white) not --ring (~1.3:1)
              'focus-visible:ring-2 focus-visible:ring-offset-2',
              '[--tw-ring-color:var(--primary-ink)]',
              // State: active
              'active:translate-y-px',
            )}
            onClick={() => handleSocialLogin(provider.key)}
          >
            <img
              src={provider.icon}
              alt={provider.name}
              className="absolute left-4 size-5 shrink-0"
              width={20}
              height={20}
            />
            <span className="w-full text-center text-base font-semibold text-foreground whitespace-nowrap">
              {provider.text}
            </span>
          </Button>
        ))}
      </div>

      {showDivider && providers.length > 0 && (
        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="bg-background px-2 text-muted-foreground">hoặc</span>
          </div>
        </div>
      )}
    </div>
  );
}
